/**
 * NOLAS 2026, © Mika (Mikaël FOURNY). Fork TNE.
 * src/hooks/useSiteSettings.ts
 *
 * Rôle :
 *   Hook de lecture des PARAMÈTRES PUBLICS du site (mode d'accès au salon,
 *   ouverture des inscriptions exposants, écran de pré-lancement, affiche).
 *   Lit `GET /api/settings/public.php` et expose un objet typé pratique en
 *   castant les valeurs (toutes servies en string brute côté serveur).
 *
 *   ZÉRO BLOCAGE : `placeholderData` = défauts SÛRS (mode 'open', signup ON,
 *   countdown ON). Si l'endpoint est absent / offline / lent, l'app reste
 *   OUVERTE, on ne ferme jamais le salon à cause d'une coupure réseau
 *   (cohérent useEventConfig.ts : on ne dégrade jamais sur défaut serveur).
 *
 * Réutilisable : 100 %, brique de configuration runtime portable sur
 *   toute app DRANE qui a un endpoint `settings/public.php`.
 *
 * Voir : api/settings/public.php, components/AccessModeGuard.tsx,
 *        pages/system/PreLaunchPage.tsx, hooks/useEventConfig.ts (modèle).
 *
 * @author Mika
 * @since  2026-06-03
 */

import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/api/client';

/** Modes d'accès au salon, pilotés au backoffice (`access.mode`). */
export type AccessMode = 'open' | 'exhibitor_only' | 'maintenance';

/** Réponse brute de `settings/public.php` (valeurs = strings). */
interface SiteSettingsResponse {
  readonly ok: true;
  readonly settings: Readonly<Record<string, string>>;
}

/** Bloc « écran de pré-lancement » dérivé des settings. */
export interface PreLaunchSettings {
  readonly title: string;
  readonly message: string;
  readonly showCountdown: boolean;
}

/** Forme typée pratique exposée aux consommateurs. */
export interface SiteSettings {
  readonly settings: Readonly<Record<string, string>>;
  readonly accessMode: AccessMode;
  readonly exhibitorSignupEnabled: boolean;
  /**
   * 2026-09-23 : l'inscription des candidats est ouverte avant l'ouverture du
   * salon (reglage `access.early_signup`). Faux par defaut, y compris quand
   * le serveur ne repond pas : une porte ne s'ouvre jamais par accident.
   */
  readonly earlySignupEnabled: boolean;
  /** Cartes d'inscription ouvertes pendant cette phase (slugs). */
  readonly earlySignupRoles: readonly string[];
  /**
   * 2026-10-04 (V141) : connexion obligatoire pour consulter le contenu
   * (`access.login_required`). VRAI par défaut, comme au serveur : un réglage
   * absent ou un serveur muet ne livre jamais le contenu à un anonyme. La
   * vraie garde reste serveur (401 `login_required`).
   */
  readonly loginRequired: boolean;
  /**
   * Vrai tant que les réglages affichés sont les défauts provisoires (le
   * serveur n'a pas encore répondu). La garde d'accès attend la vraie réponse
   * avant de renvoyer quelqu'un vers la connexion : une redirection faite sur
   * une valeur provisoire ne se rattrape pas.
   */
  readonly isPlaceholder: boolean;
  /**
   * 2026-09-25 : comptes officiels de l'evenement (reglages `social.*`, V101).
   * `null` quand le compte n'est pas declare OU quand l'adresse n'est pas une
   * adresse https : un reglage finit dans un `href`, une valeur
   * « javascript:… » y deviendrait du code execute chez le visiteur. Meme
   * regle cote serveur, `_lib/reseaux-sociaux.php`.
   */
  readonly reseauxSociaux: {
    readonly instagram: string | null;
    readonly linkedin: string | null;
  };
  readonly prelaunch: PreLaunchSettings;
  readonly posterUrl: string | null;
  readonly isLoading: boolean;
}

/**
 * Une adresse de reglage n'est rendue en lien que si elle est en https, avec
 * un hote. Tout le reste vaut `null`, sans message : un lien absent vaut
 * mieux qu'un lien dangereux.
 */
function urlHttpsOuNull(valeur: string | undefined): string | null {
  const brut = (valeur ?? '').trim();
  if (brut === '' || brut.length > 300) return null;
  try {
    const url = new URL(brut);
    if (url.protocol !== 'https:' || url.hostname === '') return null;
    if (url.username !== '' || url.password !== '') return null;
    return url.toString();
  } catch {
    return null;
  }
}

export const SITE_SETTINGS_QUERY_KEY = ['settings', 'public'] as const;

/**
 * Défauts SÛRS, utilisés tant que le serveur n'a pas répondu (placeholder)
 * et comme socle de fusion (les clés absentes côté serveur retombent ici).
 * Tout est OUVERT par défaut : aucune coupure réseau ne ferme le salon.
 */
const DEFAULT_SETTINGS: Record<string, string> = {
  'access.mode': 'open',
  'access.exhibitor_signup_enabled': '1',
  'access.prelaunch_title': 'Le salon se prépare',
  'access.prelaunch_message':
    'Le salon NOLAS ouvre bientôt ses portes. Revenez très vite !',
  'access.prelaunch_show_countdown': '1',
  'home.poster_url': '',
};

/** Cast string → booléen tolérant ('1' / 'true' / 'on' / 'yes' → true). */
function toBool(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined) return fallback;
  const v = value.trim().toLowerCase();
  if (v === '1' || v === 'true' || v === 'on' || v === 'yes') return true;
  if (v === '0' || v === 'false' || v === 'off' || v === 'no' || v === '') {
    return false;
  }
  return fallback;
}

/** Cast string → AccessMode (retombe sur 'open' si valeur inconnue). */
function toAccessMode(value: string | undefined): AccessMode {
  if (value === 'exhibitor_only' || value === 'maintenance') return value;
  return 'open';
}

/**
 * Renvoie les paramètres publics du site sous une forme typée pratique.
 * Aucun champ n'est jamais nul de façon inattendue : on fusionne toujours
 * la réponse serveur par-dessus {@link DEFAULT_SETTINGS}.
 */
export function useSiteSettings(): SiteSettings {
  const query = useQuery<SiteSettingsResponse, Error>({
    queryKey: SITE_SETTINGS_QUERY_KEY,
    queryFn: () => apiRequest<SiteSettingsResponse>('settings/public.php'),
    // Le mode d'accès bouge peu, 60 s de fraîcheur, 10 min en cache.
    staleTime: 60_000,
    gcTime: 600_000,
    // 1 retry suffit ; au-delà on garde les défauts (jamais de blocage).
    retry: 1,
    refetchOnWindowFocus: false,
    placeholderData: { ok: true, settings: DEFAULT_SETTINGS },
  });

  // Fusion défauts ← serveur : les clés absentes côté serveur conservent
  // leur valeur sûre par défaut.
  const settings: Record<string, string> = {
    ...DEFAULT_SETTINGS,
    ...(query.data?.settings ?? {}),
  };

  const rawPoster = settings['home.poster_url'];
  const posterUrl =
    typeof rawPoster === 'string' && rawPoster.trim().length > 0
      ? rawPoster.trim()
      : null;

  return {
    settings,
    accessMode: toAccessMode(settings['access.mode']),
    exhibitorSignupEnabled: toBool(
      settings['access.exhibitor_signup_enabled'],
      true,
    ),
    earlySignupEnabled: toBool(settings['access.early_signup'], false),
    earlySignupRoles: (settings['access.early_signup_roles'] ?? 'visitor')
      .split(',')
      .map((slug) => slug.trim().toLowerCase())
      .filter((slug) => slug.length > 0),
    loginRequired: toBool(settings['access.login_required'], true),
    isPlaceholder: query.isPlaceholderData,
    reseauxSociaux: {
      instagram: urlHttpsOuNull(settings['social.instagram_url']),
      linkedin: urlHttpsOuNull(settings['social.linkedin_url']),
    },
    prelaunch: {
      title: settings['access.prelaunch_title'] ?? '',
      message: settings['access.prelaunch_message'] ?? '',
      showCountdown: toBool(settings['access.prelaunch_show_countdown'], true),
    },
    posterUrl,
    isLoading: query.isLoading,
  };
}
